import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { uniq } from 'lodash';
import styled from 'styled-components';
import { Contact } from '@lgg/isomorphic/types/__generated__/graphql';
import {
  ContactFormFields,
  ContactStageAndStatus,
} from 'src/components/domain/contacts/contact-form/contact-form-fields';
import {
  CONTACT_AUTOSAVE_ON_BLUR_FIELDS,
  ContactFormValues,
  getContactDefaultValues,
  getPossibleExtraFields,
  PossibleExtraFields,
  useContactFormResolver,
} from 'src/components/domain/contacts/contact-form/contact-form.schema';
import { PossibleDuplicateContactModal } from 'src/components/domain/contacts/contact-form/possible-duplicate-contact-modal';
import { useUpdateContactAutosave } from 'src/components/domain/contacts/contact-form/use-update-contact-autosave';
import { useFormAutosave } from 'src/components/general/modals/inline-form/use-form-autosave';

const Container = styled.div`
  padding: 0 20px 10px;
`;

type ContactDetailsFormProps = {
  contact: Contact;
  variant?: 'regular' | 'light';
  onViewAll?: () => void;
};

// Editable (autosaved) version of the contact details. Render it with `key={contact.id}`
// so it re-initializes when switching contacts. While mounted it stays in sync with
// `contact` (e.g. changes saved from another instance of this form, like the other tab
// of the contact details) without overwriting the fields the user is editing.
export const ContactDetailsForm = memo<ContactDetailsFormProps>(
  ({ contact, variant = 'regular', onViewAll }) => {
    const { t } = useTranslation(['contacts']);
    const resolver = useContactFormResolver();
    const [defaultValues] = useState(() => getContactDefaultValues(contact));
    const [stageAndStatus, setStageAndStatus] = useState<ContactStageAndStatus>({
      stage: contact.stage,
      status: contact.status,
    });
    const [extraFieldsToShow, setExtraFieldsToShow] = useState<PossibleExtraFields[]>(
      () => getPossibleExtraFields(defaultValues),
    );

    const form = useForm<ContactFormValues>({
      resolver,
      defaultValues,
      reValidateMode: 'onSubmit',
    });

    const {
      onAutosave,
      possibleDuplicateContact,
      onAddAnyway,
      closePossibleDuplicateModal,
    } = useUpdateContactAutosave({ form, contactId: contact.id });

    const { onBlur: onAutosaveBlur } = useFormAutosave({
      form,
      enabled: true,
      onAutosave,
      saveOnBlurFields: CONTACT_AUTOSAVE_ON_BLUR_FIELDS,
    });

    const contactValues = useMemo(() => getContactDefaultValues(contact), [contact]);
    const isFirstSyncRef = useRef(true);

    useEffect(() => {
      if (isFirstSyncRef.current) {
        isFirstSyncRef.current = false;
        return;
      }

      // keepDirtyValues: fields with pending (not yet saved) user edits keep their value.
      form.reset(contactValues, { keepDirtyValues: true, keepErrors: true });
      setStageAndStatus({ stage: contact.stage, status: contact.status });
      setExtraFieldsToShow((fields) =>
        uniq([...fields, ...getPossibleExtraFields(contactValues)]),
      );
    }, [contactValues, contact.stage, contact.status, form]);

    return (
      <Container data-lgg-id="contact-details-form" onBlur={onAutosaveBlur}>
        <ContactFormFields
          variant={variant}
          onViewAll={onViewAll}
          form={form}
          layout="stacked"
          stageAndStatus={stageAndStatus}
          onStageAndStatusChange={setStageAndStatus}
          statusModalTitle={t('contacts:createContactModal.title.update')}
          extraFieldsToShow={extraFieldsToShow}
          onExtraFieldsToShowChange={setExtraFieldsToShow}
          defaultAddress={defaultValues.address}
        />
        {possibleDuplicateContact && (
          <PossibleDuplicateContactModal
            onClose={closePossibleDuplicateModal}
            duplicateData={possibleDuplicateContact.duplicateData}
            contact={possibleDuplicateContact.contact}
            onAddAnyway={onAddAnyway}
          />
        )}
      </Container>
    );
  },
);
